import type { JSX } from 'react';
import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { FileUpload } from '@/components/ui/file-upload';
import { useCompany } from '../hooks/useCompany';
import { useUpdateCompany } from '../hooks/useUpdateCompany';
import { useUploadLogo } from '../hooks/useUploadLogo';
import { updateCompanySchema, type UpdateCompanyFormData } from '../schemas/company.schemas';
import { CompanyFormFields } from '../components/CompanyFormFields';
import type { Company } from '../types/company.types';

const ACCEPTED_IMAGE_TYPES = 'image/png,image/jpeg,image/webp,image/svg+xml';

const toFormValues = (c: Company): UpdateCompanyFormData => ({
  name: c.name,
  email: c.email,
  phone: c.phone ?? '',
  industry: c.industry ?? '',
  companySize: c.companySize,
  country: c.country ?? '',
  city: c.city ?? '',
  address: c.address ?? '',
  description: c.description ?? '',
  website: c.website ?? '',
  taxId: c.taxId ?? '',
});

export function CompanyProfileSettingsPage(): JSX.Element {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [darkLogoFile, setDarkLogoFile] = useState<File | null>(null);

  const { company, isLoading: isLoadingCompany, isError, error } = useCompany(companyId);
  const { updateCompany, isLoading: isUpdating } = useUpdateCompany();
  const { uploadLogoAsync, uploadDarkLogoAsync, isUploading } = useUploadLogo();

  const form = useForm<UpdateCompanyFormData>({
    resolver: zodResolver(updateCompanySchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      industry: '',
      companySize: undefined,
      country: '',
      city: '',
      address: '',
      description: '',
      website: '',
      taxId: '',
    },
  });

  // Reset form when company data is loaded
  useEffect(() => {
    if (company) {
      form.reset(toFormValues(company));
    }
  }, [company, form, form.reset]);

  const onSubmit = async (data: UpdateCompanyFormData): Promise<void> => {
    if (!companyId) return;

    const requestData = { ...data, country: data.country ?? '' };

    updateCompany(
      { id: companyId, data: requestData },
      {
        onSuccess: async () => {
          try {
            const uploads: Promise<unknown>[] = [];
            if (logoFile) uploads.push(uploadLogoAsync({ companyId, file: logoFile }));
            if (darkLogoFile) uploads.push(uploadDarkLogoAsync({ companyId, file: darkLogoFile }));
            await Promise.all(uploads);
            toast.success('Company updated successfully');
            setLogoFile(null);
            setDarkLogoFile(null);
            navigate(`/companies/${companyId}/settings`);
          } catch {
            toast.error('Failed to upload logo');
          }
        },
        onError: () => toast.error('Failed to update company'),
      }
    );
  };

  if (isLoadingCompany) {
    return (
      <div className="container py-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading...</span>
      </div>
    );
  }

  if (isError || !company) {
    return (
      <div className="container py-6">
        <div className="text-center text-destructive">
          {error?.message || 'Failed to load company'}
        </div>
      </div>
    );
  }

  return (
    <div className="container py-6 space-y-6">
      <div className="flex items-center gap-4">
        <Link
          to={`/companies/${companyId}/settings`}
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Settings
        </Link>
      </div>

      <div>
        <h1 className="text-2xl font-bold">Company Profile</h1>
        <p className="text-muted-foreground">Update your company information and details</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Company Information</CardTitle>
          <CardDescription>Manage your company profile details</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <CompanyFormFields
              register={form.register}
              errors={form.formState.errors}
              onSizeChange={(v) => form.setValue('companySize', v, { shouldDirty: true })}
              onCountryChange={(v) => form.setValue('country', v, { shouldDirty: true })}
              countryValue={form.watch('country') ?? company.country ?? ''}
              sizeValue={form.watch('companySize') || company.companySize}
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <FileUpload
                label="Logo"
                accept={ACCEPTED_IMAGE_TYPES}
                value={logoFile}
                onChange={setLogoFile}
              />
              <FileUpload
                label="Dark Logo"
                accept={ACCEPTED_IMAGE_TYPES}
                value={darkLogoFile}
                onChange={setDarkLogoFile}
              />
            </div>

            <div className="flex justify-end gap-4">
              <Link to={`/companies/${companyId}/settings`}>
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              </Link>
              <Button type="submit" disabled={isUpdating || isUploading}>
                {isUpdating ? 'Saving...' : isUploading ? 'Uploading...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
